// 業種別LP の設定データ。Claude Design で生成した3HTMLを React に移植する際の差異だけ
// ここに集約し、views/IndustryLandingView.tsx は1コンポーネントで切り替える。
//
// データ更新時のルール:
// - 統計の出典は厚労省「令和6年 労働災害発生状況」(2025年公表・確定値)。
// - お客様の声・ロゴは載せない（景品表示法第5条 不当表示リスク）。
// - 関連ブログの slug は実際の docs/blog/published/ に存在するもののみを使う。

export type IndustryKey = 'construction' | 'manufacturing' | 'logistics';

export interface IndustryStat {
  /** 数字部分（大きく表示）。"232" "26,676" "墜落" のような文字列OK */
  big: string;
  /** "人" "件" のような単位（small text align-top） */
  unit?: string;
  /** "・転落" のような数字横の追記 */
  bigSuffix?: string;
  /** 説明文（2行になる場合 \n で改行） */
  description: string;
  /** 補足（前年比 etc） */
  note: string;
}

export interface IndustryStep {
  title: string;
  body: string;
}

export interface IndustryPainPoint {
  title: string;
  body: string;
}

export interface IndustryBlogLink {
  /** docs/blog/published/{slug}.md と一致すること（実在しないとビルド時に検証で落とす） */
  slug: string;
  text: string;
}

/** Hero lead テキスト。emphasis: true の要素は <strong> 相当（白色強調）でレンダリング。 */
export type HeroLeadPart = string | { emphasis: string };

export interface IndustryFourMBar {
  label: string;
  percentText: string;   // 表示 "42%"
  widthPercent: number;  // bar の幅 0-100
  /** true: アクセント色 (var(--ind))、false: fallbackColor を使う */
  isAccent: boolean;
  fallbackColor?: string;
}

export interface IndustryLandingConfig {
  key: IndustryKey;
  path: string;
  pillLabel: string;
  pillDotColor: string;
  pillTextColor: string;
  pillBg: string;
  metaTitle: string;
  metaDescription: string;
  /** 業種アクセント（--ind / --ind-strong 用） */
  indColor: string;
  indStrongColor: string;
  /** Hero の class 名（brand.css で定義） */
  heroBgClass: 'hero-con' | 'hero-man' | 'hero-log';
  heroH1Line1: string;
  heroH1Accent: string;
  heroH1Line2: string;
  /** Hero リード文。文字列と {emphasis} オブジェクトの混合配列で表現。XSS リスク無し。 */
  heroLeadParts: HeroLeadPart[];
  /** Phone モックの差し替えポイント */
  phoneTagAccent: {
    text: string;
    bg: string;
    color: string;
  };
  phoneTagOther1: string;
  phoneTagOther2: string;
  phoneSampleText: string;
  /** 4M Showcase */
  fourMLead: string;
  fourMBars: IndustryFourMBar[];
  /** Stats */
  statsEyebrow: string;
  statsHeading: string;
  stats: IndustryStat[];
  /** Pain points */
  painEyebrow: string;
  painHeading: string;
  painPoints: IndustryPainPoint[];
  /** Solution steps */
  stepsEyebrow: string;
  stepsHeading: string;
  steps: IndustryStep[];
  /** Related blog */
  blogHeading: string;
  blogLinks: IndustryBlogLink[];
  /** Final CTA */
  ctaHeadingLine1: string;
  ctaHeadingAccent: string;
  ctaHeadingLine2: string;
  ctaBody: string;
}

// =========================================================
// 建設業
// =========================================================
export const CONSTRUCTION_LP: IndustryLandingConfig = {
  key: 'construction',
  path: '/lp/construction',
  pillLabel: '建設業向け',
  pillDotColor: '#fb923c',
  pillTextColor: '#fdba74',
  pillBg: 'rgba(249,115,22,0.18)',
  metaTitle: '建設業向け｜安全ポスト＋ — 紙のヒヤリハットを、QRとAIで動く現場改善エンジンに',
  metaDescription:
    '足場・高所・重機が混在する建設現場でも、スマホとQRだけ。匿名・多言語のヒヤリハット報告から、AIによる4M分析、改善追跡までを一気通貫。元請・下請間の安全情報共有を30秒で。',
  indColor: '#f97316',
  indStrongColor: '#c2410c',
  heroBgClass: 'hero-con',
  heroH1Line1: '紙のヒヤリハットを、',
  heroH1Accent: 'QRとAI',
  heroH1Line2: 'で動く現場改善エンジンに。',
  heroLeadParts: [
    '足場・高所・重機が混在する現場でも、スマホとQRだけ。匿名・多言語の報告から',
    { emphasis: 'AIによる4M分析・改善追跡' },
    'までを一気通貫で。元請・下請間の安全情報共有を、30秒で。',
  ],
  phoneTagAccent: {
    text: '高所・足場',
    bg: 'rgba(249,115,22,.12)',
    color: '#c2410c',
  },
  phoneTagOther1: '重機・動線',
  phoneTagOther2: '開口部',
  phoneSampleText:
    '3階の開口部の手すりが一部外れたまま。あやうく踏み外しそうになった。',
  fourMLead:
    '現場からの報告は自動で4M（人・設備・作業・管理）に分類。どこに危険が偏っているかが、その場でグラフになります。',
  fourMBars: [
    { label: 'Man（人・作業行動）', percentText: '42%', widthPercent: 84, isAccent: true },
    { label: 'Machine（設備・重機）', percentText: '28%', widthPercent: 56, isAccent: false, fallbackColor: '#1e3a8a' },
    { label: 'Media（環境・動線）', percentText: '20%', widthPercent: 40, isAccent: false, fallbackColor: '#64748b' },
    { label: 'Management（管理）', percentText: '10%', widthPercent: 20, isAccent: false, fallbackColor: '#cbd5e1' },
  ],
  statsEyebrow: '建設業を取り巻く現実',
  statsHeading: '死亡災害は、いまも業種別ワースト。',
  stats: [
    {
      big: '232',
      unit: '人',
      description: '建設業の労働災害による\n死亡者数（年間・業種別最多）',
      note: '前年比 +9人',
    },
    {
      big: '約14,000',
      unit: '件',
      description: '休業4日以上の\n死傷者数（年間）',
      note: '墜落・転落・はさまれが大半',
    },
    {
      big: '墜落',
      bigSuffix: '・転落',
      description: '死亡災害の型で\n最多を占める事故',
      note: '高所・足場の対策が急務',
    },
  ],
  painEyebrow: '現場のリアルな詰まり',
  painHeading: '報告は、どこで止まっていますか。',
  painPoints: [
    {
      title: '報告が紙で止まる',
      body: 'ヒヤリハット用紙が詰所に山積み。集計されず、本店・元請に共有されないまま埋もれていく。',
    },
    {
      title: 'KYが形骸化している',
      body: '朝礼KYがハンコ作業に。新しい危険源が現場に反映されず、毎日同じ項目を読み上げるだけ。',
    },
    {
      title: '外国人作業員から声が出ない',
      body: '言語の壁と顔の見える関係で、危険の声が上がりにくい。気づいていても言い出せない。',
    },
  ],
  stepsEyebrow: '安全ポスト＋ができること',
  stepsHeading: '投稿から改善まで、3ステップ。',
  steps: [
    {
      title: 'QRで30秒投稿',
      body: '詰所・ゲートのQRを読むだけ。匿名・多言語対応で、誰でも・どの立場でも報告できる。アプリ不要。',
    },
    {
      title: 'AIが4M分析',
      body: 'Man / Machine / Media / Management に自動分類。重複や傾向を可視化し、優先すべき危険源が見える。',
    },
    {
      title: '改善を追跡',
      body: '対策の担当・期限・完了をダッシュボードで管理。元請・下請をまたいで進捗を共有できる。',
    },
  ],
  blogHeading: 'もっと知る — 建設業の安全に役立つ記事',
  blogLinks: [
    { slug: 'A08_hazard-vs-risk', text: 'ハザードとリスクの違いを、現場目線で整理する' },
    { slug: 'D04_scaffold-inspection', text: '足場点検のチェックポイント — 墜落・転落をなくす' },
    { slug: '005_ky-activity-training-guide', text: '形骸化したKY活動を立て直す進め方' },
    { slug: 'D05_opening-edge-protection', text: '開口部養生のルール — 安衛則第519条' },
    { slug: '037_foreign-worker-safety', text: '外国人作業員に伝わる安全教育のコツ' },
    { slug: '002_increase-hiyarihatto-reports', text: 'ヒヤリハット報告が集まらない、本当の理由' },
    { slug: '001_4m-analysis-workplace-safety', text: '4M分析の基本と、現場での活かし方' },
    { slug: 'D06_heavy-machinery-safety', text: '重機と作業員の接触を防ぐ動線計画' },
  ],
  ctaHeadingLine1: '紙の報告を、',
  ctaHeadingAccent: '動く改善',
  ctaHeadingLine2: 'に変える。',
  ctaBody:
    'まずはQRから1件、投稿してみてください。導入も30秒、現場の声がそのままデータになります。',
};

// =========================================================
// 製造業
// =========================================================
export const MANUFACTURING_LP: IndustryLandingConfig = {
  key: 'manufacturing',
  path: '/lp/manufacturing',
  pillLabel: '製造業向け',
  pillDotColor: '#60a5fa',
  pillTextColor: '#93c5fd',
  pillBg: 'rgba(37,99,235,0.22)',
  metaTitle: '製造業向け｜安全ポスト＋ — ライン停止の前に、危険の芽を拾う',
  metaDescription:
    'プレス・搬送・組立。多品種ラインで変わる危険源を、匿名報告とAIによる4M分析で先回り。はさまれ・巻き込まれを、ヒヤリの段階で止める。',
  indColor: '#2563eb',
  indStrongColor: '#1d4ed8',
  heroBgClass: 'hero-man',
  heroH1Line1: 'ライン停止の前に、危険の芽を拾う。',
  heroH1Accent: 'QRとAI',
  heroH1Line2: 'で。',
  heroLeadParts: [
    'プレス・搬送・組立。多品種ラインで変わる危険源を、匿名報告と',
    { emphasis: 'AIによる4M分析' },
    'で先回り。はさまれ・巻き込まれを、ヒヤリの段階で止める。',
  ],
  phoneTagAccent: {
    text: '設備・はさまれ',
    bg: 'rgba(37,99,235,.12)',
    color: '#1d4ed8',
  },
  phoneTagOther1: '搬送',
  phoneTagOther2: '組立',
  phoneSampleText:
    'プレス機の安全カバーを外したまま清掃しようとしていた。手を巻き込まれそうになった。',
  fourMLead:
    '現場からの報告は自動で 4M（人・設備・作業・管理）に分類。設備・工程ごとに危険の偏りが、その場でグラフになります。',
  fourMBars: [
    { label: 'Machine（設備）', percentText: '46%', widthPercent: 88, isAccent: true },
    { label: 'Man（人・作業行動）', percentText: '30%', widthPercent: 58, isAccent: false, fallbackColor: '#1e3a8a' },
    { label: 'Media（環境）', percentText: '16%', widthPercent: 32, isAccent: false, fallbackColor: '#64748b' },
    { label: 'Management（管理）', percentText: '8%', widthPercent: 16, isAccent: false, fallbackColor: '#cbd5e1' },
  ],
  statsEyebrow: '製造業を取り巻く現実',
  statsHeading: '死傷者数は、重点業種で最多。',
  stats: [
    {
      big: '26,676',
      unit: '人',
      description: '製造業の休業4日以上\n死傷者数（年間・重点業種で最多）',
      note: '高止まりが続く',
    },
    {
      big: '4,692',
      unit: '件',
      description: '機械による「はさまれ・\n巻き込まれ」死傷者数（年間）',
      note: '非定常作業に多発',
    },
    {
      big: '142',
      unit: '人',
      description: '製造業の労働災害による\n死亡者数（年間）',
      note: '前年比 +4人',
    },
  ],
  painEyebrow: '現場のリアルな詰まり',
  painHeading: '危険は、変わり続けています。',
  painPoints: [
    {
      title: 'はさまれ・巻き込まれが減らない',
      body: '設備の清掃や調整など非定常作業で被災。ヒヤリの段階で拾えず、同じ箇所で再発する。',
    },
    {
      title: 'ベテランの暗黙知が共有されない',
      body: '「ここは危ない」が口伝のまま。担当者の異動や退職とともに、現場の勘が失われていく。',
    },
    {
      title: '多品種・短サイクルで危険が変わる',
      body: '段取り替えのたびにリスクが変化。定型のKYでは、その日の危険源に追いつけない。',
    },
  ],
  stepsEyebrow: '安全ポスト＋ができること',
  stepsHeading: '投稿から改善まで、3ステップ。',
  steps: [
    {
      title: 'ライン脇のQRで30秒投稿',
      body: '各設備・各工程に貼ったQRを読むだけ。匿名・多言語対応で、班員も応援も気軽に報告できる。',
    },
    {
      title: 'AIが4M分析',
      body: 'Man / Machine / Media / Management に自動分類。設備・工程ごとに危険の偏りが見える。',
    },
    {
      title: '改善を追跡',
      body: '本質安全化・カバー・手順改訂など、対策の担当・期限・完了をダッシュボードで管理。',
    },
  ],
  blogHeading: 'もっと知る — 製造業の安全に役立つ記事',
  blogLinks: [
    { slug: 'C03_manufacturing-disasters', text: '製造業の労働災害 — はさまれ・巻き込まれの構造' },
    { slug: 'D12_press-safety', text: 'プレス作業の挟まれ防止と安全装置' },
    { slug: 'A14_human-error-types', text: 'ヒューマンエラーの種類 — スリップ/ラプス/ミステイク' },
    { slug: '007_risk-assessment-guide', text: '段取り替え時のリスクアセスメント' },
    { slug: '028_corrective-preventive-action', text: '4M分析で、不良と災害の両方を減らす' },
    { slug: 'A12_safety-culture', text: '改善提案が続く現場の作り方 — 安全文化の醸成' },
    { slug: 'A15_psychological-safety', text: '心理的安全性と「報告できる」現場' },
    { slug: '001_4m-analysis-workplace-safety', text: '4M分析の基本と現場での活かし方' },
  ],
  ctaHeadingLine1: 'ヒヤリを、',
  ctaHeadingAccent: '止まらないライン',
  ctaHeadingLine2: 'に変える。',
  ctaBody:
    'まずは設備のQRから1件、投稿してみてください。現場の気づきが、そのまま改善データになります。',
};

// =========================================================
// 物流業
// =========================================================
export const LOGISTICS_LP: IndustryLandingConfig = {
  key: 'logistics',
  path: '/lp/logistics',
  pillLabel: '物流・運送業向け',
  pillDotColor: '#34d399',
  pillTextColor: '#6ee7b7',
  pillBg: 'rgba(5,150,105,0.22)',
  metaTitle: '物流・運送業向け｜安全ポスト＋ — 散らばる現場の声を、ひとつの改善に',
  metaDescription:
    '荷役・配送・構内。離れた現場の声を匿名で集約し、AIが傾向を可視化。荷台からの墜落、荷役の腰痛を、ヒヤリの段階で止める。',
  indColor: '#059669',
  indStrongColor: '#047857',
  heroBgClass: 'hero-log',
  heroH1Line1: 'ドライバーが散らばっていても、危険は集まる。',
  heroH1Accent: 'QRとAI',
  heroH1Line2: 'で物流現場のヒヤリハットを。',
  heroLeadParts: [
    '荷役・配送・構内。離れた現場の声を匿名で集約し、',
    { emphasis: 'AIが傾向を可視化' },
    '。荷台からの墜落、荷役の腰痛を、ヒヤリの段階で止める。',
  ],
  phoneTagAccent: {
    text: '荷役・墜落',
    bg: 'rgba(5,150,105,.12)',
    color: '#047857',
  },
  phoneTagOther1: '構内',
  phoneTagOther2: '配送',
  phoneSampleText:
    'トラック荷台のあおりに乗って荷下ろし。足を滑らせかけてひやっとした。',
  fourMLead:
    '離れた現場からの報告も、自動で 4M（人・設備・作業・管理）に分類。拠点・配送先・荷役ごとの傾向が、その場でグラフになります。',
  fourMBars: [
    { label: 'Media（荷台・動線）', percentText: '40%', widthPercent: 80, isAccent: true },
    { label: 'Man（人・作業行動）', percentText: '34%', widthPercent: 64, isAccent: false, fallbackColor: '#1e3a8a' },
    { label: 'Machine（車両・設備）', percentText: '18%', widthPercent: 36, isAccent: false, fallbackColor: '#64748b' },
    { label: 'Management（管理）', percentText: '8%', widthPercent: 16, isAccent: false, fallbackColor: '#cbd5e1' },
  ],
  statsEyebrow: '物流業を取り巻く現実',
  statsHeading: '死傷者数は、むしろ増えている。',
  stats: [
    {
      big: '16,292',
      unit: '人',
      description: '陸上貨物運送事業の休業4日以上\n死傷者数（年間）',
      note: '前年比 +77人・増加傾向',
    },
    {
      big: '墜落',
      bigSuffix: '・転落',
      description: '災害の型で最多\n（トラック荷台等から）',
      note: '腰痛など無理な動作も多発',
    },
    {
      big: '108',
      unit: '人',
      description: '陸上貨物運送事業の\n死亡者数（年間）',
      note: '交通事故・荷役災害が中心',
    },
  ],
  painEyebrow: '現場のリアルな詰まり',
  painHeading: '声は、現場に散らばったまま。',
  painPoints: [
    {
      title: 'ドライバーが現場に散らばる',
      body: '報告のために事務所へ戻れない。配送先で感じた「危ない」が、誰にも共有されないまま消える。',
    },
    {
      title: '荷役の墜落・腰痛が絶えない',
      body: '荷台からの転落、無理な姿勢での荷役。慢性的な身体負担が、休業災害につながっていく。',
    },
    {
      title: '繁忙期に安全が後回し',
      body: '物量ピークで報告も対策も止まる。事故が起きてから、ようやく現場が動き出す。',
    },
  ],
  stepsEyebrow: '安全ポスト＋ができること',
  stepsHeading: '投稿から改善まで、3ステップ。',
  steps: [
    {
      title: '車載・倉庫のQRで30秒投稿',
      body: '運転席や倉庫ゲートのQRを読むだけ。配送先からでも、匿名・多言語でその場で報告できる。',
    },
    {
      title: 'AIが4M分析',
      body: 'Man / Machine / Media / Management に自動分類。拠点・配送先・荷役ごとに傾向が見える。',
    },
    {
      title: '改善を追跡',
      body: '荷役手順・歩車分離・荷主との調整など、対策の担当・期限・完了をダッシュボードで管理。',
    },
  ],
  blogHeading: 'もっと知る — 物流業の安全に役立つ記事',
  blogLinks: [
    { slug: 'C04_logistics-safety', text: '物流・運送業の労働安全 — 荷役・乗降のリスク' },
    { slug: 'F10_lumbago-prevention', text: '荷役作業の腰痛を減らす動作と道具' },
    { slug: '002_increase-hiyarihatto-reports', text: '散らばる現場の声を集める仕組み' },
    { slug: 'F04_overwork-prevention', text: '繁忙期でも止まらない安全活動 — 過重労働対策' },
    { slug: 'D07_forklift-safety-rules', text: '構内の歩車分離とフォークリフト災害対策' },
    { slug: 'F05_overwork-mental-health', text: 'ドライバーの体調管理と過労対策' },
    { slug: '001_4m-analysis-workplace-safety', text: '4M分析で、配送現場のリスクを可視化' },
    { slug: 'C05_warehouse-safety', text: '倉庫内のはさまれ・激突をなくす' },
  ],
  ctaHeadingLine1: '散らばる声を、',
  ctaHeadingAccent: 'ひとつの改善',
  ctaHeadingLine2: 'に。',
  ctaBody:
    'まずは車載のQRから1件、投稿してみてください。配送先の気づきが、そのまま改善データになります。',
};

export const ALL_INDUSTRY_LPS: Record<IndustryKey, IndustryLandingConfig> = {
  construction: CONSTRUCTION_LP,
  manufacturing: MANUFACTURING_LP,
  logistics: LOGISTICS_LP,
};

/** Static page generator / sitemap で参照するため、URL リスト */
export const INDUSTRY_LP_PATHS: ReadonlyArray<{ key: IndustryKey; path: string; title: string; description: string }> =
  (Object.values(ALL_INDUSTRY_LPS) as IndustryLandingConfig[]).map((c) => ({
    key: c.key,
    path: c.path,
    title: c.metaTitle,
    description: c.metaDescription,
  }));
